import { Link, useNavigate } from 'react-router-dom';
import { Bus, MapPin, Phone, Menu, X, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';

export const PublicLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="header-panashe sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/panashe-logo.jpg" 
                alt="Panashe Coaches" 
                className="h-10 w-10 rounded-lg object-cover"
              />
              <div>
                <span className="font-heading text-xl font-bold tracking-tight">PANASHE</span>
                <span className="text-panashe-gold ml-1 font-heading text-xl">COACHES</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-white/80 hover:text-white transition-colors" data-testid="nav-home">
                Home
              </Link>
              <Link to="/track" className="text-white/80 hover:text-white transition-colors" data-testid="nav-track">
                Track Bus
              </Link>
              <a href="tel:+263771234567" className="text-white/80 hover:text-white transition-colors flex items-center gap-1">
                <Phone className="h-4 w-4" />
                <span>+263 77 123 4567</span>
              </a>
              
              {user ? (
                <div className="flex items-center gap-3">
                  <span className="text-white/80 text-sm">{user.full_name}</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-panashe-gold text-panashe-gold hover:bg-panashe-gold hover:text-slate-900"
                    onClick={() => navigate(user.role === 'passenger' ? '/profile' : user.role === 'overseer' ? '/admin' : '/dashboard')}
                    data-testid="dashboard-btn"
                  >
                    {user.role === 'passenger' ? 'My Dashboard' : 'Dashboard'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-white/80 hover:text-white"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-panashe-gold text-panashe-gold hover:bg-panashe-gold hover:text-slate-900"
                    data-testid="login-btn"
                  >
                    <User className="h-4 w-4 mr-1" />
                    Login
                  </Button>
                </Link>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-panashe-blue border-t border-white/10 animate-fade-in">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/" 
                className="block text-white/80 hover:text-white py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/track" 
                className="block text-white/80 hover:text-white py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Track Bus
              </Link>
              {user ? (
                <>
                  <Link 
                    to={user.role === 'passenger' ? '/profile' : user.role === 'overseer' ? '/admin' : '/dashboard'}
                    className="block text-panashe-gold py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {user.role === 'passenger' ? 'My Dashboard' : 'Dashboard'}
                  </Link>
                  <button 
                    className="block text-white/80 hover:text-white py-2 w-full text-left"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="block text-panashe-gold py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/panashe-logo.jpg" 
                  alt="Panashe Coaches" 
                  className="h-10 w-10 rounded-lg object-cover"
                />
                <span className="font-heading text-lg font-bold">PANASHE COACHES</span>
              </div>
              <p className="text-slate-400 text-sm">
                Premium cross-border bus services connecting Zimbabwe and South Africa since 2010.
              </p>
            </div>
            
            <div>
              <h4 className="font-heading font-bold mb-4 text-panashe-gold">ROUTES</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Harare - Durban via Masvingo
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Harare - Durban via Bulawayo
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Durban - Harare
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-bold mb-4 text-panashe-gold">CONTACT</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="h-3 w-3" />
                  +263 77 123 4567
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-3 w-3" />
                  +27 31 234 5678
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-6 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} Panashe Coaches. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
